import {
    type CollisionDetection,
    DndContext,
    DragOverlay,
    type DragStartEvent,
    MouseSensor,
    PointerSensor,
    pointerWithin,
    useSensor,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import * as React from 'react';

import { hideOnDrag } from '@gitbook/react-uikit';
import { Portal } from '@gitbook/spine-react';

import { DragOverlayPreview } from './DragOverlayPreview';
import { PageDragPreview, type PageDragPreviewRef } from './PageDragPreview';
import type { DNDItem } from './types';

type ActiveBlockDragItem = Extract<DNDItem, { type: 'block' | 'block-range' }>;

const ActiveBlockDragContext = React.createContext<ActiveBlockDragItem | null | undefined>(
    undefined
);

export function useActiveBlockDrag(): ActiveBlockDragItem | null {
    const activeBlockDrag = React.useContext(ActiveBlockDragContext);
    if (activeBlockDrag === undefined) {
        throw new Error('useActiveBlockDrag must be used within DndProvider');
    }
    return activeBlockDrag;
}

/** Prefer the smallest overlapping drop target so nested targets win over their containers. */
const preferSmallestPointerWithin: CollisionDetection = (args) => {
    const collisions = pointerWithin(args);
    if (collisions.length <= 1) {
        return collisions;
    }

    return [...collisions]
        .sort((a, b) => {
            const rectA = args.droppableRects.get(a.id);
            const rectB = args.droppableRects.get(b.id);
            const areaA = rectA ? rectA.width * rectA.height : Number.POSITIVE_INFINITY;
            const areaB = rectB ? rectB.width * rectB.height : Number.POSITIVE_INFINITY;
            return areaA - areaB;
        })
        .slice(0, 1); // just take the smallest collision rectangle with the pointer in
};

export function DndProvider(props: { children: React.ReactNode }) {
    const [active, setActive] = React.useState<DNDItem | null>(null);
    const [usesPageDragPreview, setUsesPageDragPreview] = React.useState(false);
    const hiddenOnDragElements = React.useRef(new Set<Element>());
    const hiddenOnDragObserverRef = React.useRef<MutationObserver | null>(null);
    const pageDragPreviewRef = React.useRef<PageDragPreviewRef>(null);

    React.useEffect(
        () => () => {
            hiddenOnDragObserverRef.current?.disconnect();
            restoreHiddenOnDragElements(hiddenOnDragElements.current);
        },
        []
    );

    const pointerSensor = useSensor(PointerSensor, {
        activationConstraint: {
            distance: 10,
        },
    });

    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 10,
        },
    });

    const onDragStart = React.useCallback((event: DragStartEvent) => {
        const activeItem = event.active.data.current as DNDItem;
        setUsesPageDragPreview(
            activeItem.type === 'page' && Boolean(pageDragPreviewRef.current?.isPrepared())
        );

        // Disconnect any existing observer
        hiddenOnDragObserverRef.current?.disconnect();

        // Keep track of selected elements that must be hidden during drag
        hideElementsDuringDrag(hiddenOnDragElements.current);
        hiddenOnDragObserverRef.current = observeElementsMountedDuringDrag(
            hiddenOnDragElements.current
        );

        setActive(activeItem);
    }, []);

    const onDragEnd = React.useCallback(() => {
        pageDragPreviewRef.current?.hide();

        // Disconnect the observer and restore any hidden elements
        hiddenOnDragObserverRef.current?.disconnect();
        hiddenOnDragObserverRef.current = null;
        restoreHiddenOnDragElements(hiddenOnDragElements.current);

        // no logic here, as we use useDroppableWithCallback to localize drop actions
        setUsesPageDragPreview(false);
        setActive(null);
    }, []);

    const activeBlockDrag = isActiveBlockDragItem(active) ? active : null;

    return (
        <ActiveBlockDragContext.Provider value={activeBlockDrag}>
            <DndContext
                sensors={[pointerSensor, mouseSensor]}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragCancel={onDragEnd}
                /**
                 * The default collision algorithm will not work for some ToC lines where the
                 * Draggable elements have bigger rects than the droppable areas.
                 * Here we switch to using pointerWithin, which is not affected by draggable
                 * and droppable not fully intersecting.
                 *
                 * See docs:
                 * https://docs.dndkit.com/api-documentation/context-provider/collision-detection-algorithms#pointer-within
                 */
                collisionDetection={preferSmallestPointerWithin}
            >
                {props.children}
                <Portal>
                    <DragOverlay
                        modifiers={[snapCenterToCursor]}
                        /**
                         * Drop animations cannot properly work for our current implementation.
                         *
                         * They require the DragOverlay, or items being dragged, actually moving
                         * to the new destination in the DOM without unmounting.
                         *
                         * See this discussion for more info:
                         * https://github.com/clauderic/dnd-kit/discussions/409
                         */
                        dropAnimation={null}
                    >
                        {active ? (
                            <DragOverlayPreview item={active} hidden={usesPageDragPreview} />
                        ) : null}
                    </DragOverlay>
                </Portal>
            </DndContext>
            <Portal>
                <PageDragPreview ref={pageDragPreviewRef} />
            </Portal>
        </ActiveBlockDragContext.Provider>
    );
}

/** Keep page drags out of the editor-only drag context. */
function isActiveBlockDragItem(item: DNDItem | null): item is ActiveBlockDragItem {
    return item?.type === 'block' || item?.type === 'block-range';
}

/** Hide only elements that explicitly opt out while a drag is active. */
function hideElementsDuringDrag(hiddenElements: Set<Element>): void {
    restoreHiddenOnDragElements(hiddenElements);
    hideElementsWithin(document, hiddenElements);
}

/**
 * During drag we hide some UI elements (e.g. controls).
 * Some of those elements (those not in the viewport) are virtualized when a drag starts.
 * This observer keeps track of those elements when they mount during a drag (during auto-scrolling)
 * to apply the `hidden-on-drag` attribute on them. It is disconnected as soon as the drag ends.
 */
function observeElementsMountedDuringDrag(hiddenElements: Set<Element>): MutationObserver {
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const addedNode of mutation.addedNodes) {
                if (addedNode instanceof Element) {
                    hideElementsWithin(addedNode, hiddenElements);
                }
            }
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
}

function hideElementsWithin(root: Document | Element, hiddenElements: Set<Element>): void {
    const selector = `[data-hide-on-drag], .${hideOnDrag}`;
    if (root instanceof Element && root.matches(selector)) {
        hideElementDuringDrag(root, hiddenElements);
    }
    for (const element of root.querySelectorAll(selector)) {
        hideElementDuringDrag(element, hiddenElements);
    }
}

function hideElementDuringDrag(element: Element, hiddenElements: Set<Element>): void {
    if (!hiddenElements.has(element)) {
        element.toggleAttribute(HIDDEN_ON_DRAG_ATTRIBUTE, true);
        hiddenElements.add(element);
    }
}

/** Restore every element hidden by the current drag, including after cancellation or unmounting. */
function restoreHiddenOnDragElements(hiddenElements: Set<Element>): void {
    for (const element of hiddenElements) {
        element.removeAttribute(HIDDEN_ON_DRAG_ATTRIBUTE);
    }
    hiddenElements.clear();
}

const HIDDEN_ON_DRAG_ATTRIBUTE = 'data-drag-hidden';
